/**
 * Top-level team picker entry point. Reads `mode` from the
 * TeamSelectionProvider and:
 *  - returns `null` in `hidden` mode (no UI surface)
 *  - renders a "Active Team" trigger row + a Modal hosting the curated
 *    list (`linked`) or search typeahead (`all`)
 *  - exposes a "Clear active team" row inside the Modal when one is set
 *
 * Designed to live inside the Settings > Manage submenu so the
 * demo-flexibility feature surfaces without occupying header real estate.
 * @module features/team-selection/components/TeamSelector
 */
import React, { useCallback, useState } from "react";

import TeamSelectorView from "./TeamSelectorView";

import { TeamSelectionMode } from "@/generated/graphql";
import { useTeamSelection } from "@/providers/TeamSelectionProvider";

/**
 * Props for {@link TeamSelectorContainer}.
 */
interface TeamSelectorContainerProps {
  /**
   * Optional callback fired after the picker modal closes — whether a team
   * was picked, cleared, or the modal was simply dismissed. Lets a host
   * menu (e.g. the header Settings popover) close itself so the user lands
   * back in the app instead of the menu reappearing behind the picker.
   */
  readonly onDismiss?: () => void;
}

/**
 * Container for the team-picker dispatcher. Drives the trigger label,
 * modal open state, and decides which mode-specific child renders inside.
 * @param root0 - Component props
 * @param root0.onDismiss - Optional callback fired after the picker closes
 * @returns The rendered dispatcher (or `null` in hidden mode)
 */
const TeamSelectorContainer = ({
  onDismiss,
}: TeamSelectorContainerProps = {}) => {
  const { mode, activeTeamId, activeTeamName, isLoaded, clearTeam } =
    useTeamSelection();
  const [isOpen, setIsOpen] = useState(false);

  const handleOpen = useCallback(() => setIsOpen(true), []);
  const handleClose = useCallback(() => {
    setIsOpen(false);
    onDismiss?.();
  }, [onDismiss]);
  const handleClear = useCallback(() => {
    void clearTeam()
      .then(() => {
        setIsOpen(false);
        onDismiss?.();
      })
      .catch(error => {
        // Keep the modal open on failure so the user can retry without
        // losing context.
        console.warn("Failed to clear active team:", error);
      });
  }, [clearTeam, onDismiss]);

  if (!isLoaded || mode === TeamSelectionMode.Hidden) {
    return null;
  }

  const label = activeTeamName ?? "Select an active team";
  const hasActiveTeam = activeTeamId !== null;

  return (
    <TeamSelectorView
      mode={mode}
      label={label}
      isOpen={isOpen}
      onOpen={handleOpen}
      onClose={handleClose}
      hasActiveTeam={hasActiveTeam}
      onClear={handleClear}
    />
  );
};

export default TeamSelectorContainer;
